import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys, type UnitMember, type MembershipStatus } from '@vtak/shared';
import { workspaceClient } from '@/services/workspace-client';
import { VirtualRowList } from '@/components/common/virtual-row-list';
import { Button } from '@/components/ui/button';

/** Query key for a unit's members filtered by status. */
function membersKey(unitId: string, status: MembershipStatus): readonly unknown[] {
  return [...queryKeys.workspace.members(unitId), status];
}

/** Pending membership requests with one-click approve / deny. */
export function PendingList({ unitId }: { unitId: string }): React.JSX.Element {
  const query = useQuery({
    queryKey: membersKey(unitId, 'pending'),
    queryFn: () => workspaceClient.getMembers(unitId, 'pending'),
  });
  const decide = useDecision(unitId);

  if (query.isLoading) {
    return <p className="p-4 text-center text-xs text-muted-foreground">Loading requests…</p>;
  }

  return (
    <VirtualRowList<UnitMember>
      items={query.data ?? []}
      rowHeight={68}
      keyOf={(m) => m.id}
      emptyText="No pending requests"
      renderRow={(member) => (
        <PendingRow member={member} onDecide={(status) => decide.mutate({ id: member.id, status })} busy={decide.isPending} />
      )}
    />
  );
}

/** A single pending request row. */
function PendingRow({
  member,
  onDecide,
  busy,
}: {
  member: UnitMember;
  onDecide: (status: MembershipStatus) => void;
  busy: boolean;
}): React.JSX.Element {
  return (
    <div className="mx-2 mb-1 rounded-md bg-white/[0.04] px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-mono text-xs text-foreground">{shortSub(member.keycloakSub)}</p>
          <p className="text-[10px] text-muted-foreground">
            via {member.joinedVia} · {formatDate(member.createdAt)}
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <Button size="sm" className="h-7 px-2.5 text-xs" disabled={busy} onClick={() => onDecide('approved')}>
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2.5 text-xs text-red-400 hover:bg-red-500/10"
            disabled={busy}
            onClick={() => onDecide('denied')}
          >
            Deny
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Approve/deny mutation invalidating both the pending and approved lists. */
function useDecision(unitId: string): ReturnType<typeof useMutation<{ ok: boolean }, Error, { id: string; status: MembershipStatus }>> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: MembershipStatus }) =>
      workspaceClient.decideMember(unitId, id, status),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: membersKey(unitId, 'pending') });
      void client.invalidateQueries({ queryKey: membersKey(unitId, 'approved') });
    },
  });
}

/** Shorten a Keycloak subject id for display. */
function shortSub(sub: string): string {
  return sub.length > 12 ? `${sub.slice(0, 12)}…` : sub;
}

/** Format an ISO date as a short local date-time. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}
